import { Component } from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';

@Component({
    selector: 'app-import-global-propulsion-forecast',
    templateUrl: './import-global-propulsion-forecast.component.html',
    styleUrl: './import-global-propulsion-forecast.component.scss',
    standalone: false
})
export class ImportGlobalPropulsionForecastComponent {
  showSpinner: boolean = false;
  isFullFile: boolean = true;
  file: File;
  sheets: string[];
  sheetNames: string[];
  toolTip = 'The expected format that the uploaded input file should be in.\n\n' +
    'Full File: This file format is the file that is generated from the Global Propulsion Forecast Template report. ' +
    'This file format expects the PROP_SCEN_ID and PROP_SCEN_DTL_ID column, ' +
    'as well as any column for the calendar years.\n\n' +
    'Short File: This file format is a file that expects the following columns to be present: PROP_SCEN_ID, PROP_SCEN_DTL_ID, CY, PEN';

  constructor(public readonly excelService: ExcelExportService,
              public readonly propulsionScenarioService: PropulsionScenarioService,
              public readonly stateService: StateService,
              public snackBar: FabMessageService,
              public dialog: MatDialog) {
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheetNames = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.sheetNames.length === 0) {
          this.file = null;
          this.snackBar.displayError("Please re-upload file", DISMISS_MSG)
        } else {
          this.importGlobalPropForecast();
        }
      }
    );
  }

  /**
   * Imports the global propulsion forecast input file
   */
  importGlobalPropForecast(): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Import',
        message: 'The following import will update data. Are you sure you want to import the file?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'warning',
        actionColor: 'primary'
      },
      disableClose: true
    });
    dialogRef.componentInstance.yes.subscribe(() => {
      this.showSpinner = true;
      this.propulsionScenarioService.importGlobalPropForecast(this.file, this.sheetNames, this.isFullFile).subscribe(
        () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('File imported successfully', DISMISS_MSG);
        },
        error => {
          this.showSpinner = false;
          this.snackBar.displayError(error, DISMISS_MSG);
        }
      );
    });
  }
}
